import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import { SERVER_URL } from "../../../constants";
import { whoIsArriving, whoIsLeaving } from "../../Transfers/api/transfersApi";
import {
  getActivityGroupsByDate,
  getActivityLeaders,
} from "../../Activities/api/activitiesApi";
import {
  fetchClassGroups,
  fetchClassLists,
} from "../../Classes/api/classesApi";
import { getWeeklyStaffScheduling } from "../../Timetable/api/staffSchedulingApi";
import { mondayOfWeek } from "../../Timetable/utils/timetableUtils";
import { hasRealAllergy } from "../../Lists/allergyFilters";
import {
  fetchBedroomResidents,
  fetchBedrooms,
} from "../../Bedrooms/api/bedroomAllocationApi";

// On-demand report that fans out across several endpoints, so bound the range.
export const MAX_RANGE_DAYS = 14;

export function flagTrue(v) {
  return v === true || v === 1 || ["true", "yes", "y", "1"].includes(String(v).toLowerCase());
}

// Qualifications shown for each staff member (✓ columns + count line). Resolved
// from a forward-looking `qualifications` array if the backend provides one,
// otherwise from the existing `dsl`/`firstaid` booleans and the LIFEGUARD role.
export const STAFF_QUALIFICATIONS = [
  { code: "DSL", label: "DSL", countLabel: "DSL on site" },
  { code: "FIRSTAID", label: "First Aid", countLabel: "First aiders" },
  { code: "LIFEGUARD", label: "Lifeguard", countLabel: "Lifeguards" },
];

function normQual(value) {
  return String(value || "").toUpperCase().replace(/[^A-Z]/g, "");
}

export function staffQualSet(member) {
  const set = new Set();
  if (Array.isArray(member?.qualifications)) {
    member.qualifications.forEach((q) => set.add(normQual(q)));
  }
  if (flagTrue(member?.dsl)) set.add("DSL");
  if (flagTrue(member?.firstaid)) set.add("FIRSTAID");
  (member?.roles || []).forEach((r) => {
    if (normQual(r) === "LIFEGUARD") set.add("LIFEGUARD");
  });
  return set;
}

export function hasQual(member, code) {
  return staffQualSet(member).has(code);
}

export function staffResidential(member) {
  return flagTrue(member?.residential) || flagTrue(member?.isResident);
}

// Chips shown next to a staff member's name: their qualifications plus a
// Residential attribute chip. `kind` lets the UI colour attributes differently.
export function staffChips(member) {
  const chips = STAFF_QUALIFICATIONS.filter((q) => hasQual(member, q.code)).map((q) => ({
    code: q.code,
    label: q.label,
    kind: "qual",
  }));
  if (staffResidential(member)) {
    chips.push({ code: "RESIDENTIAL", label: "Residential", kind: "attr" });
  }
  return chips;
}

// Whether any staff row carries the dsl/firstaid flags yet (backend may not have
// added them). Used to show/hide the safeguarding count line.
export function staffSafeguardingKnown(days) {
  return (days || []).some((d) =>
    (d.staff || []).some((s) => s.dsl != null || s.firstaid != null)
  );
}

// Staff job categories (matched against the `roles` array, case/format tolerant).
// First match wins. LIFEGUARD is a qualification, not a category, so it's ignored
// for bucketing. Anything unmatched falls into "Other staff".
const STAFF_CATEGORIES = [
  { key: "camp_manager", label: "Camp Manager", test: (r) => /camp.?manager/.test(r) },
  { key: "house_parents", label: "House Parents", test: (r) => /residential.?manager|house.?parent|welfare/.test(r) },
  { key: "teachers", label: "Teachers", test: (r) => /teacher/.test(r) },
  { key: "activity", label: "Activity Staff", test: (r) => /activit/.test(r) },
];
const QUALIFICATION_ROLES = new Set(["lifeguard"]);

function staffCategoryKey(member) {
  const roles = (member.roles || []).map((r) => String(r).toLowerCase().trim());
  for (const cat of STAFF_CATEGORIES) {
    if (roles.some((r) => !QUALIFICATION_ROLES.has(r) && cat.test(r))) return cat.key;
  }
  return "other";
}

// Group staff into ordered, non-empty job-category buckets.
export function groupStaffByCategory(staff) {
  const buckets = new Map();
  STAFF_CATEGORIES.forEach((c) => buckets.set(c.key, []));
  buckets.set("other", []);

  (staff || []).forEach((m) => buckets.get(staffCategoryKey(m)).push(m));

  const ordered = [...STAFF_CATEGORIES, { key: "other", label: "Other staff" }];
  return ordered
    .map((c) => ({ key: c.key, label: c.label, members: buckets.get(c.key) }))
    .filter((g) => g.members.length > 0);
}

function sameCampus(value, campusName) {
  return String(value || "") === String(campusName || "");
}

function genderBucket(value) {
  const s = String(value || "").trim().toUpperCase();
  if (["M", "MALE", "BOY", "B"].includes(s)) return "male";
  if (["F", "FEMALE", "GIRL", "G"].includes(s)) return "female";
  return "unknown";
}

// Overnight resident: only count people staying on site. Exclude rows that are
// explicitly non-residential; include `true`/unknown so it still works before
// the backend adds the flag.
function isOvernightResident(row) {
  const v = row.residential ?? row.isResident ?? row.overnight;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return ["true", "1", "yes", "y"].includes(v.toLowerCase());
  return null; // field absent
}

function isStaffAdult(row) {
  const v = row.staff ?? row.isStaff;
  return v === true || v === 1 || String(v).toLowerCase() === "true";
}

function bucketKey(row) {
  if (String(row.personType).toUpperCase() === "STUDENT") return "students";
  return isStaffAdult(row) ? "staff" : "adults";
}

// Breakdown by type + gender, counting only overnight residents.
function buildBreakdown(rows) {
  const empty = () => ({ total: 0, male: 0, female: 0, unknown: 0 });
  const out = { students: empty(), staff: empty(), adults: empty(), total: 0 };

  (rows || []).forEach((row) => {
    if (isOvernightResident(row) === false) return; // skip explicit non-residents
    const key = bucketKey(row);
    const g = genderBucket(row.gender ?? row.sex ?? row.studentGender);
    out[key][g] += 1;
    out[key].total += 1;
    out.total += 1;
  });

  return out;
}

// "Students: 18 (4 boys, 14 girls) · Staff: 8 (4 male, 4 female)". Gender split
// is only shown when known, so it stays clean before the backend supplies it.
export function formatBreakdown(breakdown) {
  if (!breakdown || breakdown.total === 0) return "None";

  const seg = (label, bucket, maleWord, femaleWord) => {
    if (!bucket || bucket.total === 0) return null;
    const sub = [];
    if (bucket.male) sub.push(`${bucket.male} ${maleWord}`);
    if (bucket.female) sub.push(`${bucket.female} ${femaleWord}`);
    const detail = sub.length ? ` (${sub.join(", ")})` : "";
    return `${label}: ${bucket.total}${detail}`;
  };

  return [
    seg("Students", breakdown.students, "boys", "girls"),
    seg("Staff", breakdown.staff, "male", "female"),
    seg("Adults", breakdown.adults, "male", "female"),
  ]
    .filter(Boolean)
    .join(" · ");
}

function dayInRange(day, start, end) {
  if (!start || !end) return false;
  const d = dayjs(day);
  const s = dayjs(start);
  const e = dayjs(end);
  if (!d.isValid() || !s.isValid() || !e.isValid()) return false;
  return (
    (d.isSame(s, "day") || d.isAfter(s, "day")) &&
    (d.isSame(e, "day") || d.isBefore(e, "day"))
  );
}

// On-site headcount per campus/type for a day — same source as the Residents
// page (api/residentcount/{date}).
async function fetchResidentCount(dayStr) {
  const token = sessionStorage.getItem("bearer");
  const res = await fetch(`${SERVER_URL}api/residentcount/${dayStr}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 204) return [];
  if (!res.ok) throw new Error(`Failed to load resident count (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

function onSiteRank(type) {
  const t = String(type || "").toLowerCase();
  if (t.startsWith("student")) return 0;
  if (t.startsWith("adult")) return 1;
  if (t.startsWith("staff")) return 2;
  return 3;
}

export function formatOnSite(onSite) {
  if (!onSite || onSite.length === 0) return "None";
  return onSite.map((o) => `${o.count} ${o.type}`).join(", ");
}

// Natural sort so "2" comes before "10" (e.g. east 1, east 2 … east 10).
function naturalCompare(a, b) {
  return String(a || "").localeCompare(String(b || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function bareRoomName(b) {
  return b.bedroomName || b.roomName || b.groupName || `Room ${b.id}`;
}

function roomResidentIds(room) {
  return new Set((room?.residents || []).map((r) => r.id));
}

// Build the bedrooms summary for a day by diffing against the previous day's
// board (occupancy "freed as of today"):
// - inUse: rooms occupied tonight, count = occupied / capacity
// - movingIn: rooms someone is in tonight who wasn't yesterday (beds filled)
// - vacating: rooms someone was in yesterday who isn't tonight (beds freed)
function buildBedroomSummary(todayBoard, prevBoard) {
  const rooms = Array.isArray(todayBoard) ? todayBoard : [];
  const prevById = new Map(
    (Array.isArray(prevBoard) ? prevBoard : []).map((b) => [b.id, b])
  );

  const inUse = rooms
    .filter((b) => (b.residents || []).length > 0)
    .map((b) => ({
      house: b.house || "",
      name: bareRoomName(b),
      count: (b.residents || []).length,
      capacity: Number(b.capacity) || 0,
    }));

  const movingIn = [];
  const vacating = [];

  rooms.forEach((b) => {
    const todayIds = roomResidentIds(b);
    const prevRoom = prevById.get(b.id);
    const prevIds = roomResidentIds(prevRoom);

    const movedIn = (b.residents || []).filter((r) => !prevIds.has(r.id)).length;
    const vacated = (prevRoom?.residents || []).filter((r) => !todayIds.has(r.id)).length;

    const base = {
      house: b.house || "",
      name: bareRoomName(b),
      capacity: Number(b.capacity) || 0,
    };
    if (movedIn > 0) movingIn.push({ ...base, count: movedIn });
    if (vacated > 0) vacating.push({ ...base, count: vacated });
  });

  return { inUse, movingIn, vacating };
}

// Group in-use rooms by house for display.
export function groupRoomsByHouse(inUse) {
  const map = new Map();
  (inUse || []).forEach((r) => {
    const key = r.house || "Unassigned";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  });
  return Array.from(map.entries())
    .map(([house, list]) => ({
      house,
      rooms: list.slice().sort((a, b) => naturalCompare(a.name, b.name)),
    }))
    .sort((a, b) => a.house.localeCompare(b.house));
}

// In-use room name, with used/capacity only when the room is shared (>1 in it).
export function formatRoomName(r) {
  return r.count > 1 ? `${r.name} (${r.count}/${r.capacity})` : r.name;
}

async function fetchBoardForDay(dayStr, campusName) {
  const data = await fetchBedroomResidents({ date: dayStr }).catch(() => null);
  const list = Array.isArray(data) ? data : data?.residents || [];
  return fetchBedrooms({ date: dayStr, campus: campusName, residents: list }).catch(() => []);
}

async function fetchAllergiesRange(startStr, endStr) {
  const token = sessionStorage.getItem("bearer");
  const res = await fetch(
    `${SERVER_URL}api/allergies?start=${startStr}&end=${endStr}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw new Error(`Failed to load allergies (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Assemble a per-day operational summary for one campus across a date range,
 * stitched together from the existing transfers / activities / classes /
 * allergies / staff-scheduling endpoints (no dedicated backend endpoint).
 */
export default function useDailySummary({ campusName, startDate, endDate }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [truncated, setTruncated] = useState(false);

  const startStr = startDate ? dayjs(startDate).format("YYYY-MM-DD") : null;
  const endStr = endDate ? dayjs(endDate).format("YYYY-MM-DD") : null;

  const dayList = useMemo(() => {
    if (!startStr || !endStr) return [];
    const start = dayjs(startStr);
    const end = dayjs(endStr);
    if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) return [];

    const out = [];
    let cursor = start;
    while (
      (cursor.isSame(end, "day") || cursor.isBefore(end, "day")) &&
      out.length < MAX_RANGE_DAYS
    ) {
      out.push(cursor.format("YYYY-MM-DD"));
      cursor = cursor.add(1, "day");
    }
    return out;
  }, [startStr, endStr]);

  const totalRequestedDays = useMemo(() => {
    if (!startStr || !endStr) return 0;
    const diff = dayjs(endStr).diff(dayjs(startStr), "day") + 1;
    return diff > 0 ? diff : 0;
  }, [startStr, endStr]);

  const load = useCallback(async () => {
    if (!campusName || dayList.length === 0) {
      setDays([]);
      setError(null);
      setTruncated(false);
      return;
    }

    setLoading(true);
    setError(null);
    setTruncated(totalRequestedDays > dayList.length);

    try {
      // Range-level fetches (once). Query the whole year for allergies — the
      // endpoint keys off arrival within the queried window, so a narrow daily
      // range would miss people who arrived earlier but are still on site. We
      // then filter per day by their stay (dayInRange) below.
      const allergyStart = dayjs(dayList[0]).startOf("year").format("YYYY-MM-DD");
      const allergyEnd = dayjs(dayList[dayList.length - 1]).endOf("year").format("YYYY-MM-DD");
      const allergyRows = (await fetchAllergiesRange(allergyStart, allergyEnd))
        .filter((r) => sameCampus(r.campus, campusName) && hasRealAllergy(r.allergies));

      // Distinct Monday-anchored weeks covering the range.
      const weekStarts = Array.from(
        new Set(dayList.map((d) => mondayOfWeek(d)?.format("YYYY-MM-DD")).filter(Boolean))
      );
      const staffWeeks = await Promise.all(
        weekStarts.map((ws) =>
          getWeeklyStaffScheduling(campusName, ws).catch(() => null)
        )
      );
      const staffRows = staffWeeks
        .filter(Boolean)
        .flatMap((wk) => (Array.isArray(wk?.rows) ? wk.rows : []));

      // Bedroom boards for the range PLUS the day before, so each day can be
      // diffed against the previous to find beds moved into / freed.
      const boardDays = [
        dayjs(dayList[0]).subtract(1, "day").format("YYYY-MM-DD"),
        ...dayList,
      ];
      const boardEntries = await Promise.all(
        boardDays.map(async (d) => [d, await fetchBoardForDay(d, campusName)])
      );
      const boardByDay = new Map(boardEntries);

      // Per-day fetches, batched.
      const perDay = await Promise.all(
        dayList.map(async (dayStr) => {
          const [arr, dep, groups, classGroups, residentCounts, leaders, classLists] =
            await Promise.all([
              whoIsArriving(dayStr).catch(() => []),
              whoIsLeaving(dayStr).catch(() => []),
              getActivityGroupsByDate(dayStr).catch(() => []),
              fetchClassGroups(campusName, dayStr, dayStr).catch(() => []),
              fetchResidentCount(dayStr).catch(() => []),
              getActivityLeaders(dayStr, campusName).catch(() => []),
              fetchClassLists({ startDate: dayStr, endDate: dayStr }, campusName).catch(() => []),
            ]);

          // Resolve activity-group leader names from the leaders endpoint by
          // staffId (the groups payload only carries leaderStaffId).
          const leaderById = new Map(
            (leaders || []).map((l) => [String(l.staffId), l.name])
          );

          const onSite = (residentCounts || [])
            .filter((rc) => sameCampus(rc.campus, campusName))
            .map((rc) => ({ type: rc.residentType, count: rc.residentCount }))
            .sort((a, b) => onSiteRank(a.type) - onSiteRank(b.type));

          const prevDayStr = dayjs(dayStr).subtract(1, "day").format("YYYY-MM-DD");
          const bedrooms = buildBedroomSummary(
            boardByDay.get(dayStr) || [],
            boardByDay.get(prevDayStr) || []
          );

          const arrivals = buildBreakdown(
            (arr || []).filter((t) => sameCampus(t.campus, campusName))
          );

          const departures = buildBreakdown(
            (dep || []).filter((t) => sameCampus(t.campus, campusName))
          );

          const dayClasses = (classGroups || []).filter((c) =>
            Array.isArray(c.groupDates) ? c.groupDates.includes(dayStr) : true
          );

          // Student count + teacher per class, from the class lists.
          const studentsByClass = new Map();
          const teacherByClass = new Map();
          (Array.isArray(classLists) ? classLists : []).forEach((s) => {
            const key = String(s.group_id);
            studentsByClass.set(key, (studentsByClass.get(key) || 0) + 1);
            if (!teacherByClass.has(key) && s.adult_full_name) {
              teacherByClass.set(key, s.adult_full_name);
            }
          });

          const classes = dayClasses
            .map((c) => ({
              name: c.groupName || "",
              students: studentsByClass.get(String(c.id)) || 0,
              teacher: c.leaderName || teacherByClass.get(String(c.id)) || "",
            }))
            .sort((a, b) => String(a.name).localeCompare(String(b.name)));

          const activityGroups = (groups || [])
            .filter((g) => sameCampus(g.campus, campusName))
            .map((g) => {
              const members = Array.isArray(g.members) ? g.members : [];
              const adults = members.filter(
                (m) => String(m?.personType).toUpperCase() === "ADULT"
              ).length;
              const slot = String(g.slot || "");
              const external = String(g.productType || "").toUpperCase() === "EXTERNAL";
              return {
                name: g.name,
                slot,
                external,
                typeLabel: `${slot.toLowerCase()}${slot ? " - " : ""}${
                  external ? "external" : "onsite"
                }`,
                total: members.length,
                adults,
                students: members.length - adults,
                leader:
                  g.leaderName || leaderById.get(String(g.leaderStaffId)) || "",
              };
            })
            // Evening slots always follow afternoon/day; then external before
            // onsite; then by name.
            .sort((a, b) => {
              const rank = (s) => (String(s).toUpperCase() === "EVENING" ? 1 : 0);
              return (
                rank(a.slot) - rank(b.slot) ||
                (a.external === b.external ? 0 : a.external ? -1 : 1) ||
                String(a.name || "").localeCompare(String(b.name || ""))
              );
            });

          const allergies = allergyRows
            .filter((r) => dayInRange(dayStr, r.startDate, r.endDate))
            .map((r) => ({
              firstName: r.firstName,
              lastName: r.lastName,
              allergies: r.allergies,
              personType: r.personType,
            }));

          const staff = staffRows
            .filter(
              (r) =>
                r.staff === true &&
                Array.isArray(r.scheduledDates) &&
                r.scheduledDates.map((d) => dayjs(d).format("YYYY-MM-DD")).includes(dayStr) &&
                dayInRange(dayStr, r.arrivalDate, r.departureDate)
            )
            // dedupe by nsrId (weeks can overlap the range)
            .filter((r, i, all) => all.findIndex((x) => x.nsrId === r.nsrId) === i)
            .map((r) => ({
              name: r.name,
              roles: Array.isArray(r.roles) ? r.roles : [],
              dsl: r.dsl,
              firstaid: r.firstaid,
              qualifications: r.qualifications,
              residential: r.residential,
              isResident: r.isResident,
            }))
            .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

          return {
            date: dayStr,
            onSite,
            arrivals,
            departures,
            bedrooms,
            classCount: dayClasses.length,
            classes,
            activityGroups,
            allergies,
            staff,
          };
        })
      );

      setDays(perDay);
    } catch (e) {
      console.error("[useDailySummary] failed:", e);
      setError(e);
      setDays([]);
    } finally {
      setLoading(false);
    }
  }, [campusName, dayList, totalRequestedDays]);

  useEffect(() => {
    load();
  }, [load]);

  return { days, loading, error, truncated, maxDays: MAX_RANGE_DAYS, refetch: load };
}
